import type { ProgramFormat } from "@travily/types/models";
import type { IconColor, IconName } from "@ui/components/Icon";
import type { ToggleTileColor } from "@ui/components/ToggleTile";

const NS = "components.views.programs.search";

export type ProgramAvailabilityFilterValue = "any" | "sessions" | "flexible";

export type RecommendedFilterKey = "dropOff" | "community" | "familyFavourite" | "featured";

export interface RecommendedFilterDef {
  key: RecommendedFilterKey;
  labelKey: string;
  icon: IconName;
  color: ToggleTileColor;
}

// i18n-tasks-use t('components.views.programs.search.recommended_drop_off')
// i18n-tasks-use t('components.views.programs.search.recommended_community')
// i18n-tasks-use t('components.views.programs.search.recommended_family_favourite')
// i18n-tasks-use t('components.views.programs.search.recommended_featured')
export const RECOMMENDED_FILTERS: RecommendedFilterDef[] = [
  { key: "dropOff", labelKey: `${NS}.recommended_drop_off`, icon: "backpack", color: "green" },
  { key: "community", labelKey: `${NS}.recommended_community`, icon: "community", color: "purple" },
  { key: "familyFavourite", labelKey: `${NS}.recommended_family_favourite`, icon: "heart", color: "pink" },
  { key: "featured", labelKey: `${NS}.recommended_featured`, icon: "crown", color: "yellow" },
];

export interface InfluenceFilterDef {
  /** Matches a value in `Program::INFLUENCES` (backend enum). */
  value: string;
  labelKey: string;
  /** Drives both the tile tint and the lightbulb icon's color prop. */
  color: Extract<ToggleTileColor, IconColor>;
}

// i18n-tasks-use t('components.views.programs.search.influence_worldschooling')
// i18n-tasks-use t('components.views.programs.search.influence_montessori')
// i18n-tasks-use t('components.views.programs.search.influence_waldorf_steiner')
// i18n-tasks-use t('components.views.programs.search.influence_forest_school')
// i18n-tasks-use t('components.views.programs.search.influence_self_directed')
// i18n-tasks-use t('components.views.programs.search.influence_curriculum_based')
// i18n-tasks-use t('components.views.programs.search.influence_faith_based')
export const INFLUENCE_FILTERS: InfluenceFilterDef[] = [
  { value: "worldschooling", labelKey: `${NS}.influence_worldschooling`, color: "purple" },
  { value: "montessori", labelKey: `${NS}.influence_montessori`, color: "purple" },
  { value: "waldorf_steiner", labelKey: `${NS}.influence_waldorf_steiner`, color: "green" },
  { value: "forest_school", labelKey: `${NS}.influence_forest_school`, color: "green" },
  { value: "self_directed", labelKey: `${NS}.influence_self_directed`, color: "pink" },
  { value: "curriculum_based", labelKey: `${NS}.influence_curriculum_based`, color: "pink" },
  { value: "faith_based", labelKey: `${NS}.influence_faith_based`, color: "yellow" },
];

export interface FormatFilterDef {
  value: ProgramFormat;
  labelKey: string;
  icon: IconName;
  /** Drives both the tile tint and the icon's color prop. */
  color: Extract<ToggleTileColor, IconColor>;
}

// i18n-tasks-use t('components.views.programs.search.format_year_round')
// i18n-tasks-use t('components.views.programs.search.format_seasonal')
// i18n-tasks-use t('components.views.programs.search.format_popup')
// i18n-tasks-use t('components.views.programs.search.format_travelling')
// i18n-tasks-use t('components.views.programs.search.format_online')
export const FORMAT_OPTIONS: FormatFilterDef[] = [
  { value: "year_round", labelKey: `${NS}.format_year_round`, icon: "date", color: "purple" },
  { value: "seasonal", labelKey: `${NS}.format_seasonal`, icon: "tree", color: "green" },
  { value: "popup", labelKey: `${NS}.format_popup`, icon: "confetti", color: "pink" },
  { value: "travelling", labelKey: `${NS}.format_travelling`, icon: "compass", color: "yellow" },
  { value: "online", labelKey: `${NS}.format_online`, icon: "globeStand", color: "purple" },
];

/**
 * Curated common instruction languages shown in the modal (design set). Each is
 * an ISO 639-1 code within `Program::INSTRUCTION_LANGUAGES`;
 * labels are resolved at render via `localizedLanguageName`.
 */
export const INSTRUCTION_LANGUAGE_CODES = [
  "en",
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "zh",
  "ja",
  "ko",
  "ar",
  "nl",
  "sv",
] as const;

// i18n-tasks-use t('components.views.programs.search.availability_any')
// i18n-tasks-use t('components.views.programs.search.availability_sessions')
// i18n-tasks-use t('components.views.programs.search.availability_flexible')
export const AVAILABILITY_OPTIONS: { value: ProgramAvailabilityFilterValue; labelKey: string }[] = [
  { value: "any", labelKey: `${NS}.availability_any` },
  { value: "sessions", labelKey: `${NS}.availability_sessions` },
  { value: "flexible", labelKey: `${NS}.availability_flexible` },
];
